import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ApiService, showToast } from './api.service';
import { jobStatusClass } from './job-status-style';

type JobsControlTab = 'intake' | 'dispatch' | 'field' | 'blocked' | 'quotes' | 'office' | 'closed';

@Component({
  selector: 'app-admin-jobs-control',
  standalone: true,
  imports: [CommonModule, RouterLink],
  template: `
    <div class="min-h-full admin-premium-bg">
      <div class="max-w-[1700px] mx-auto p-5">
        <div class="flex flex-col lg:flex-row lg:items-end justify-between gap-3 mb-5">
          <div>
            <p class="text-[9px] font-bold uppercase tracking-[0.16em] text-[#1A6FA6] mb-1">Dispatch Board</p>
            <h1 class="text-xl font-semibold text-slate-950 tracking-tight">Jobs Control</h1>
            <p class="text-slate-500 text-xs">One operating view for intake, dispatch, field work, quote follow-up, office review, and closed jobs.</p>
          </div>
          <div class="flex flex-wrap gap-2">
            <a routerLink="/admin/dispatch/unassigned" class="h-9 px-3 rounded-md border border-slate-200 bg-white text-slate-600 text-xs font-bold flex items-center gap-2 hover:bg-slate-50">
              <i class="ti ti-plus"></i> Create / Allocate
            </a>
            <button type="button" (click)="loadJobs()" [disabled]="isLoading()" class="h-9 px-3 rounded-md bg-[#0C3A5A] text-white text-xs font-bold flex items-center gap-2 hover:bg-[#082d46] disabled:opacity-60">
              <i class="ti" [class.ti-loader]="isLoading()" [class.animate-spin]="isLoading()" [class.ti-refresh]="!isLoading()"></i> Refresh
            </button>
          </div>
        </div>

        <div class="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2.5 mb-4">
          @for (stat of summaryStats(); track stat.label) {
            <button type="button" (click)="activeTab.set(stat.tab)" class="bg-white rounded-xl border shadow-sm px-3.5 py-2.5 text-left transition-colors"
                    [ngClass]="activeTab() === stat.tab ? 'border-[#9ED4EF] ring-2 ring-sky-100' : 'border-[#E7EEF5] hover:border-slate-300'">
              <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">{{ stat.label }}</p>
              <p class="text-lg font-semibold mt-0.5" [ngClass]="stat.className">{{ stat.count }}</p>
            </button>
          }
        </div>

        <div class="mb-4 rounded-xl border border-[#E7EEF5] bg-white shadow-sm p-1 flex flex-wrap gap-1">
          @for (tab of tabs; track tab.key) {
            <button type="button" (click)="activeTab.set(tab.key)" class="h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-2 transition-colors"
                    [ngClass]="activeTab() === tab.key ? 'bg-[#0C3A5A] text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'">
              <i class="ti" [class]="'ti-' + tab.icon"></i>
              {{ tab.label }}
              <span class="text-[10px] opacity-70">{{ tabCount(tab.key) }}</span>
            </button>
          }
        </div>

        <div class="mb-4 rounded-lg border px-4 py-3 flex flex-col lg:flex-row lg:items-center justify-between gap-3" [ngClass]="tabBannerClass()">
          <div>
            <p class="text-[10px] font-bold uppercase tracking-[0.12em]">{{ activeTabMeta().label }}</p>
            <p class="text-[12px] font-semibold text-slate-800 mt-0.5">{{ activeTabMeta().description }}</p>
          </div>
          <span class="px-2 py-1 rounded-md bg-white/80 border border-white text-[10px] font-bold text-slate-600 w-fit">{{ filteredJobs().length }} job{{ filteredJobs().length === 1 ? '' : 's' }}</span>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
          @if (isLoading()) {
            @for (n of skeletons; track n) {
              <div class="bg-white rounded-xl border border-slate-200 p-4 animate-pulse">
                <div class="h-4 w-24 bg-slate-100 rounded mb-3"></div>
                <div class="h-5 w-3/4 bg-slate-100 rounded mb-2"></div>
                <div class="h-4 w-1/2 bg-slate-50 rounded mb-5"></div>
                <div class="h-20 bg-slate-50 rounded-lg"></div>
              </div>
            }
          } @else {
            @for (job of filteredJobs(); track job.id) {
              <article class="bg-white rounded-xl border border-slate-200 shadow-sm p-4 hover:shadow-md hover:-translate-y-0.5 transition-all min-w-0">
                <div class="flex items-start justify-between gap-3">
                  <div class="min-w-0">
                    <div class="flex flex-wrap items-center gap-1.5 mb-2">
                      <span [ngClass]="statusClass(job.status)">{{ job.status || 'Unknown' }}</span>
                      @if (job.priority === 'Urgent') {
                        <span class="px-2 py-0.5 rounded-md bg-rose-50 text-rose-700 border border-rose-100 text-[10px] font-bold">URGENT</span>
                      }
                      @if (job.latest_safety_status === 'Declined') {
                        <span class="px-2 py-0.5 rounded-md bg-rose-50 text-rose-700 border border-rose-100 text-[10px] font-bold">SAFETY</span>
                      }
                      @if (+job.materials_blocking_count > 0) {
                        <span class="px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-100 text-[10px] font-bold">PARTS</span>
                      } @else if (+job.materials_ready_count > 0) {
                        <span class="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-100 text-[10px] font-bold">MATERIALS READY</span>
                      }
                      @if (scheduleBadge(job); as badge) {
                        <span class="px-2 py-0.5 rounded-md border text-[10px] font-bold" [ngClass]="badge.className">{{ badge.label }}</span>
                      }
                      @if (job.feedback_rating) {
                        <span class="px-2 py-0.5 rounded-md border text-[10px] font-bold flex items-center gap-1"
                              [ngClass]="feedbackBadgeClass(job.feedback_rating)">
                          <i class="ti ti-star-filled"></i>{{ job.feedback_rating }}/5
                        </span>
                      }
                    </div>
                    <h3 class="text-sm font-bold text-slate-950 truncate">#{{ job.id }} {{ job.customer_name || 'Customer' }}</h3>
                    <p class="text-xs text-slate-500 truncate mt-0.5">{{ job.address || job.type || 'No address recorded' }}</p>
                  </div>
                  <a [routerLink]="['/admin/dispatch/job', job.id]" class="h-8 px-3 rounded-md bg-[#EAF5FA] text-[#1A6FA6] text-[10px] font-bold flex items-center gap-1.5 no-underline hover:bg-[#D5EBF6]">
                    <i class="ti ti-eye"></i> Open
                  </a>
                </div>

                <div class="grid grid-cols-2 gap-2 mt-3">
                  <div class="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 min-w-0">
                    <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Owner</p>
                    <p class="text-xs font-semibold text-slate-700 truncate">{{ job.assigned_workers || 'No worker assigned' }}</p>
                  </div>
                  <div class="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 min-w-0">
                    <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Stage Age</p>
                    <p class="text-xs font-semibold text-slate-700 truncate">{{ stageAgeLabel(job) }}</p>
                  </div>
                </div>

                @if (job.feedback_rating) {
                  <div class="mt-3 rounded-lg border px-3 py-2" [ngClass]="feedbackPanelClass(job.feedback_rating)">
                    <div class="flex items-center justify-between gap-2">
                      <p class="text-[10px] font-bold uppercase tracking-widest">Customer Feedback</p>
                      <span class="text-xs font-bold">{{ job.feedback_rating }}/5</span>
                    </div>
                    <div class="flex items-center gap-0.5 mt-1" [attr.aria-label]="job.feedback_rating + ' out of 5 stars'">
                      @for (star of feedbackStars; track star) {
                        <i class="ti ti-star-filled text-sm" [ngClass]="star <= +job.feedback_rating ? '' : 'opacity-20'"></i>
                      }
                    </div>
                    <p class="text-xs font-semibold mt-1 line-clamp-2">{{ job.feedback_comments || 'Customer submitted a rating without comments.' }}</p>
                  </div>
                }

                <div class="mt-3 rounded-lg border px-3 py-2" [ngClass]="nextActionClass(job)">
                  <p class="text-[10px] font-bold uppercase tracking-widest opacity-70">Next Action</p>
                  <p class="text-xs font-semibold mt-0.5">{{ nextActionLabel(job) }}</p>
                </div>

                @if (job.status === 'Paused') {
                  <div class="mt-3 rounded-lg border border-orange-100 bg-orange-50 px-3 py-2">
                    <p class="text-[10px] font-bold text-orange-600 uppercase tracking-widest">{{ job.pause_category || 'Pause Reason' }}</p>
                    <p class="text-xs font-semibold text-orange-900 mt-0.5 line-clamp-2">{{ job.pause_remarks || 'No pause remarks recorded.' }}</p>
                  </div>
                }

                @if (+job.materials_count > 0) {
                  <div class="mt-3 rounded-lg border px-3 py-2" [ngClass]="materialReadinessClass(job)">
                    <p class="text-[10px] font-bold uppercase tracking-widest">Materials</p>
                    <p class="text-xs font-semibold mt-0.5">{{ materialReadinessLabel(job) }}</p>
                  </div>
                }

                @if (+job.schedule_change_count > 0) {
                  <div class="mt-3 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2">
                    <p class="text-[10px] font-bold text-amber-700 uppercase tracking-widest">Last Schedule Change</p>
                    <p class="text-xs font-semibold text-amber-900 mt-0.5 line-clamp-2">{{ job.last_schedule_change_category }}: {{ job.last_schedule_change_remarks || 'Reason recorded' }}</p>
                  </div>
                }

                @if (job.type === 'Inspection' || job.linked_inspection_id) {
                  <div class="mt-3 rounded-lg border border-sky-100 bg-sky-50 px-3 py-2">
                    <p class="text-[10px] font-bold text-[#1A6FA6] uppercase tracking-widest">Inspection / Service</p>
                    <p class="text-xs font-semibold text-slate-700 mt-0.5">{{ inspectionFlowLabel(job) }}</p>
                  </div>
                }
              </article>
            } @empty {
              <div class="col-span-full bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center">
                <i class="ti ti-clipboard-check text-4xl text-slate-300"></i>
                <h2 class="text-lg font-semibold text-slate-900 mt-3">No jobs in this stage</h2>
                <p class="text-sm text-slate-500 mt-1">Choose another tab or create a new job from intake.</p>
              </div>
            }
          }
        </div>
      </div>
    </div>
  `
})
export class AdminJobsControlComponent implements OnInit {
  private api = inject(ApiService);

  jobs = signal<any[]>([]);
  isLoading = signal(false);
  activeTab = signal<JobsControlTab>('dispatch');
  skeletons = [1, 2, 3, 4, 5, 6, 7, 8];
  readonly feedbackStars = [1, 2, 3, 4, 5];

  tabs: Array<{ key: JobsControlTab; label: string; icon: string; description: string }> = [
    { key: 'intake', label: 'Intake', icon: 'inbox', description: 'New jobs waiting for a worker and attendance time.' },
    { key: 'dispatch', label: 'Dispatch', icon: 'calendar-check', description: 'Scheduled and approved jobs that need clean field handoff.' },
    { key: 'field', label: 'In Field', icon: 'tool', description: 'Jobs currently being worked by the field team.' },
    { key: 'blocked', label: 'Blocked', icon: 'alert-circle', description: 'Paused, unsafe, or declined jobs that need manager attention.' },
    { key: 'quotes', label: 'Quotes', icon: 'file-dollar', description: 'Inspection quotes waiting for customer approval or accepted quotes needing scheduling.' },
    { key: 'office', label: 'Office Review', icon: 'clipboard-data', description: 'Field-complete jobs waiting for office review and close-out.' },
    { key: 'closed', label: 'Closed', icon: 'circle-check', description: 'Completed or terminated jobs.' }
  ];

  filteredJobs = computed(() => this.jobs().filter(job => this.jobInTab(job, this.activeTab())));

  summaryStats = computed(() => [
    { label: 'Unassigned', count: this.tabCount('intake'), tab: 'intake' as JobsControlTab, className: 'text-rose-700' },
    { label: 'Needs Schedule', count: this.jobs().filter(job => this.needsScheduling(job)).length, tab: 'dispatch' as JobsControlTab, className: 'text-amber-700' },
    { label: 'In Field', count: this.tabCount('field'), tab: 'field' as JobsControlTab, className: 'text-emerald-700' },
    { label: 'Blocked', count: this.tabCount('blocked'), tab: 'blocked' as JobsControlTab, className: 'text-orange-700' },
    { label: 'Quotes', count: this.tabCount('quotes'), tab: 'quotes' as JobsControlTab, className: 'text-cyan-700' },
    { label: 'Office Review', count: this.tabCount('office'), tab: 'office' as JobsControlTab, className: 'text-[#1A6FA6]' },
    { label: 'Closed', count: this.tabCount('closed'), tab: 'closed' as JobsControlTab, className: 'text-slate-700' }
  ]);

  ngOnInit() {
    this.loadJobs();
  }

  loadJobs() {
    this.isLoading.set(true);
    this.api.getJobs().subscribe({
      next: (res: any) => {
        this.isLoading.set(false);
        this.jobs.set(res.success && res.data?.jobs ? res.data.jobs : []);
      },
      error: () => {
        this.isLoading.set(false);
        this.jobs.set([]);
        showToast('error', 'Failed to load jobs control.');
      }
    });
  }

  tabCount(tab: JobsControlTab): number {
    return this.jobs().filter(job => this.jobInTab(job, tab)).length;
  }

  activeTabMeta() {
    return this.tabs.find(tab => tab.key === this.activeTab()) || this.tabs[0];
  }

  statusClass(status: string): string {
    return jobStatusClass(status);
  }

  feedbackBadgeClass(rating: number | string): string {
    const value = Number(rating);
    if (value <= 2) return 'bg-rose-50 text-rose-700 border-rose-100';
    if (value === 3) return 'bg-amber-50 text-amber-700 border-amber-100';
    return 'bg-emerald-50 text-emerald-700 border-emerald-100';
  }

  feedbackPanelClass(rating: number | string): string {
    const value = Number(rating);
    if (value <= 2) return 'border-rose-100 bg-rose-50 text-rose-800';
    if (value === 3) return 'border-amber-100 bg-amber-50 text-amber-800';
    return 'border-emerald-100 bg-emerald-50 text-emerald-800';
  }

  tabBannerClass(): string {
    const tab = this.activeTab();
    if (tab === 'blocked') return 'border-orange-100 bg-orange-50 text-orange-800';
    if (tab === 'office') return 'border-sky-100 bg-sky-50 text-[#1A6FA6]';
    if (tab === 'field') return 'border-emerald-100 bg-emerald-50 text-emerald-800';
    if (tab === 'quotes') return 'border-cyan-100 bg-cyan-50 text-cyan-800';
    return 'border-slate-100 bg-slate-50 text-slate-700';
  }

  nextActionLabel(job: any): string {
    if (job.latest_safety_status === 'Declined') return 'Resolve safety concern before work continues';
    if (+job.materials_blocking_count > 0) return `Resolve parts readiness: ${job.latest_material_blocker || 'parts pending'}`;
    if (job.status === 'Unassigned') return 'Allocate worker and schedule attendance';
    if (job.status === 'Scheduled') return 'Worker to start shift and begin job';
    if (job.status === 'In Progress') return 'Monitor field proof, materials, and completion';
    if (job.status === 'Paused') return `${job.pause_category || 'Paused'}: ${job.pause_remarks || 'Review pause reason and help worker resume'}`;
    if (job.status === 'Quote Sent') return 'Follow up customer approval';
    if (job.status === 'Approved' && this.inspectionNeedsServiceDecision(job)) return 'Create linked service job or record no service required';
    if (job.status === 'Approved') return this.needsScheduling(job) ? 'Schedule approved work and assign worker' : 'Worker can start approved work';
    if (job.status === 'Ready for Office') return 'Review safety, photos, notes, materials, quote, then close';
    if (job.status === 'Completed') return 'Closed. Confirm feedback follow-up if required';
    if (job.status === 'Terminated') return 'Closed as terminated';
    return 'Review job detail';
  }

  nextActionClass(job: any): string {
    if (job.latest_safety_status === 'Declined' || job.status === 'Paused' || +job.materials_blocking_count > 0) return 'bg-orange-50 text-orange-800 border-orange-100';
    if (job.status === 'Unassigned' || this.needsScheduling(job)) return 'bg-amber-50 text-amber-800 border-amber-100';
    if (job.status === 'Ready for Office') return 'bg-sky-50 text-sky-800 border-sky-100';
    if (job.status === 'Completed') return 'bg-emerald-50 text-emerald-800 border-emerald-100';
    return 'bg-slate-50 text-slate-700 border-slate-100';
  }

  stageAgeLabel(job: any): string {
    const value = job.status === 'Scheduled' ? job.scheduled_date
      : job.status === 'In Progress' ? job.started_at
      : job.status === 'Paused' ? (job.pause_started_at || job.paused_at)
      : ['Ready for Office', 'Completed'].includes(job.status) ? job.completed_at
      : job.created_at;
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return 'Unknown';
    const minutes = Math.max(0, Math.floor((Date.now() - date.getTime()) / 60000));
    if (minutes < 60) return `${minutes || 1} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hr`;
    const days = Math.floor(hours / 24);
    return `${days} day${days === 1 ? '' : 's'}`;
  }

  inspectionFlowLabel(job: any): string {
    if (job.type === 'Inspection') {
      if (job.inspection_service_decision === 'No Service Required') return 'No follow-up service required';
      if (job.status === 'Quote Sent') return 'Inspection quote sent, waiting for customer';
      if (job.status === 'Approved') return 'Quote accepted, service decision required';
      if (job.status === 'Completed') return 'Inspection complete';
      return 'Inspection job, quote/service handoff applies';
    }
    if (job.linked_inspection_id) return `Service work linked to inspection ${job.linked_inspection_id}`;
    return 'Standard service job';
  }

  materialReadinessLabel(job: any): string {
    const blocking = Number(job.materials_blocking_count || 0);
    const ready = Number(job.materials_ready_count || 0);
    const used = Number(job.materials_used_count || 0);
    const total = Number(job.materials_count || 0);
    if (blocking > 0) return `${blocking}/${total} part item(s) need action: ${job.latest_material_blocker || 'pending'}`;
    if (ready > 0 && used === 0) return `${ready}/${total} part item(s) ready for field use`;
    if (used > 0) return `${used}/${total} material item(s) used`;
    return `${total} material item(s) logged`;
  }

  materialReadinessClass(job: any): string {
    if (Number(job.materials_blocking_count || 0) > 0) return 'border-amber-100 bg-amber-50 text-amber-800';
    if (Number(job.materials_ready_count || 0) > 0) return 'border-emerald-100 bg-emerald-50 text-emerald-800';
    return 'border-slate-100 bg-slate-50 text-slate-700';
  }

  scheduleBadge(job: any): { label: string; className: string } | null {
    if (this.needsScheduling(job)) return { label: 'NEEDS SCHEDULE', className: 'bg-amber-50 text-amber-700 border-amber-100' };
    if (Number(job.materials_blocking_count || 0) > 0 && ['Unassigned', 'Scheduled', 'Approved'].includes(job.status)) {
      return { label: 'SCHEDULE RISK', className: 'bg-orange-50 text-orange-700 border-orange-100' };
    }
    if (Number(job.schedule_change_count || 0) > 0) return { label: 'RESCHEDULED', className: 'bg-violet-50 text-violet-700 border-violet-100' };
    if (job.scheduled_date && ['Scheduled', 'Approved'].includes(job.status)) return { label: 'SCHEDULED', className: 'bg-sky-50 text-sky-700 border-sky-100' };
    return null;
  }

  private jobInTab(job: any, tab: JobsControlTab): boolean {
    if (tab === 'intake') return job.status === 'Unassigned';
    if (tab === 'dispatch') return job.status === 'Scheduled' || this.needsScheduling(job);
    if (tab === 'field') return job.status === 'In Progress';
    if (tab === 'blocked') return job.status === 'Paused' || job.latest_safety_status === 'Declined' || +job.materials_blocking_count > 0 || job.status === 'Terminated';
    if (tab === 'quotes') return ['Quote Sent', 'Approved'].includes(job.status);
    if (tab === 'office') return job.status === 'Ready for Office';
    if (tab === 'closed') return ['Completed', 'Terminated'].includes(job.status);
    return false;
  }

  private needsScheduling(job: any): boolean {
    if (job.status !== 'Approved') return false;
    if (this.inspectionNeedsServiceDecision(job)) return false;
    return !job.scheduled_date || !job.assigned_workers;
  }

  private inspectionNeedsServiceDecision(job: any): boolean {
    return job.type === 'Inspection'
      && job.status === 'Approved'
      && job.inspection_service_decision !== 'No Service Required'
      && !job.linked_service_count;
  }
}
